import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {UserResignationDgSearchResults} from "../models/datagrid-search-results/user-resignation.dg-search-results.model";
import {UserResignation} from "../models/user-resignation.model";

/**
 * Serwis warstwy danych zarządzający wnioskami o usunięcie konta (Resignation Requests).
 * Odpowiada za procesowanie żądań zgodności z RODO (GDPR), w tym bezpieczną
 * anonimizację danych osobowych pacjentów/użytkowników z zachowaniem integralności
 * historycznych danych medycznych i finansowych.
 */
@Injectable({
  providedIn: 'root'
})
export class UserResignationService implements DataGridServiceInterface<Observable<UserResignationDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę wniosków o usunięcie konta zgłoszonych przez użytkowników.
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny, według której aplikowane jest sortowanie.
   * @param showDeletedObjects Flaga decydująca o widoczności zarchiwizowanych/odrzuconych wniosków.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla komponentu DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UserResignationDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/res-req/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const userResignationResults = new UserResignationDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new UserResignation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return userResignationResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje i filtruje wnioski o rezygnację w oparciu o zaawansowane parametry.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę wniosków dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UserResignationDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/res-req/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new UserResignationDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new UserResignation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje nieodwracalny proces anonimizacji danych osobowych powiązanych z kontem.
   * Zastępuje wrażliwe dane (np. imię, nazwisko, e-mail) ciągami znaków maskujących,
   * zachowując relacje w bazie danych (np. zagregowane statystyki sprzedażowe).
   *
   * @param uuid Unikalny identyfikator procedowanego wniosku o rezygnację.
   * @param sendEmail Flaga decydująca o wysłaniu ostatecznego potwierdzenia usunięcia konta przed zamaskowaniem adresu e-mail.
   * @returns Observable emitujący zaktualizowany obiekt wniosku ze statusem zakończenia procedury.
   */
  anonymizeUser(uuid: string, sendEmail: boolean) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/res-req-proceed/${uuid}/${sendEmail}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserResignation>(new UserResignation(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Weryfikuje autentyczność żądania usunięcia konta przed rozpoczęciem
   * właściwego procesu anonimizacji (za pomocą wpisania uuid użytkownika do usunięcia).
   *
   * @param uuid Identyfikator użytkownika.
   * @param uuid2 Ręcznie wpisany uuid potwierdzający użytkownika do usunięcia.
   * @returns Observable emitujący zweryfikowany obiekt wniosku.
   */
  checkUser(uuid: any, uuid2: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/res-req-check/${uuid}/${uuid2}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserResignation>(new UserResignation(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
